import React, { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Navigate, useLocation } from 'react-router-dom';
import { buildLoginRedirectPath } from '../utils/returnUrl';
import { canUserAccessNavigationPath } from '../utils/navigationAccess';
import Layout from './Layout';
import AccessDeniedPage from '../pages/misc/AccessDeniedPage';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, permissionsReady, user } = useAuth();
  const location = useLocation();

  const pathAllowed = useMemo(() => {
    if (!user || !permissionsReady) return true;
    return canUserAccessNavigationPath(user, location.pathname);
  }, [user, permissionsReady, location.pathname]);

  // Only block the tree on initial auth. Permission refreshes must not unmount Layout —
  // that wiped the "refresh once" flag and looped Loading ↔ home for users with no modules.
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" style={{ color: 'var(--color-text-secondary)' }}>
          Loading...
        </Typography>
      </Box>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to={buildLoginRedirectPath(location)} replace />;
  }

  if (!pathAllowed) {
    return (
      <Layout>
        <AccessDeniedPage />
      </Layout>
    );
  }

  return children;
};

export default ProtectedRoute;
